// Email Service for Box.OS Contact Form
// This service handles communication with the email server API

interface ContactFormData {
  name: string
  email: string
  company?: string
  projectType: string
  budget?: string
  timeline?: string
  message: string
}

interface EmailResponse {
  success: boolean
  message: string
  reference?: string
  error?: string
}

/**
 * Sends a contact form submission to the email server
 * @param formData - The contact form data
 * @returns Promise with the API response
 */
export const sendContactEmail = async (formData: ContactFormData): Promise<EmailResponse> => {
  try {
    console.log('📤 Sending email via Netlify Function...')

    // Call the Netlify Function instead of the API directly
    // This keeps the API key secret on the server-side
    const response = await fetch('/.netlify/functions/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    })

    console.log('📥 Response status:', response.status, response.statusText)

    // Parse the response
    const data = await response.json()
    console.log('📄 Response data:', data)

    if (!response.ok) {
      throw new Error(data.error || `Server error: ${response.status}`)
    }

    console.log('✅ Email sent successfully!')
    return {
      success: true,
      message: data.message || 'Your message has been sent successfully!',
      reference: data.reference
    }

  } catch (error) {
    console.error('❌ Email service error:', error)

    return {
      success: false,
      message: 'Failed to send your message. Please try again or email us directly.',
      error: error instanceof Error ? error.message : 'Unknown error'
    }
  }
}

/**
 * Validates the email server connection
 * @returns Promise with health check status
 */
export const checkEmailServiceHealth = async (): Promise<boolean> => {
  try {
    const apiUrl = import.meta.env.VITE_EMAIL_API_URL

    if (!apiUrl) {
      return false
    }

    const response = await fetch(`${apiUrl}/health`)
    return response.ok
  } catch (error) {
    console.error('Email service health check failed:', error)
    return false
  }
}
